import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { currencyFormat, PF } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Link, useNavigate } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { VendorProducts } from 'api/products/_requests';
import { getAllCouponByVendor, VendorCoupons } from 'api/coupon/_requests';

const EcomVendorCouponTable = ({ vendorId }: { vendorId?: string }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [couponsData, setCouponsData] = useState<VendorCoupons[]>([]);

  const handleEdit = (couponId: string) => {
    navigate(`/edit-coupon/${couponId}`);
  };

  const fetchProductsData = async () => {
    const response = await getAllCouponByVendor(vendorId as string);
    setCouponsData(response);
  };

  const columns: ColumnDef<VendorCoupons>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row: { original } }) => {
        const { code } = original;
        return <p className="mb-0">{code}</p>;
      },
      meta: {
        headerProps: { style: { width: '20%' }, className: 'text-start' },
        cellProps: { className: 'text-start fw-semibold' }
      }
    },
    {
      accessorKey: 'discountType',
      header: () => 'Discount Type',
      cell: ({ row: { original } }) => {
        const { discountType } = original;
        return <p className="mb-0">{discountType}</p>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-center' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'value',
      header: () => 'Value',
      cell: ({ row: { original } }) => {
        const { value } = original;
        return <p className="mb-0">{value}</p>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-center' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'appliesTo',
      header: () => 'Applied To',
      cell: ({ row: { original } }) => {
        const { appliesTo } = original;
        return <p className="mb-0">{appliesTo}</p>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-center' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'usageLimit',
      header: () => 'Usage Limit',
      cell: ({ row: { original } }) => {
        const { usageLimit } = original;
        return <p className="mb-0">{usageLimit}</p>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-center' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'usedCount',
      header: () => 'Used Count',
      cell: ({ row: { original } }) => {
        const { usedCount } = original;
        return <p className="mb-0">{usedCount}</p>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-center' },
        cellProps: { className: 'text-center fw-semibold' }
      }
    },
    {
      accessorKey: 'action',
      header: () => 'Action',
      cell: ({ row: { original } }) => {
        return (
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleEdit(original._id)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'text-end' },
        cellProps: { className: 'text-end fw-semibold' }
      }
    }
  ];

  useEffect(() => {
    fetchProductsData();
  }, []);

  const table = useAdvanceTable({
    data: couponsData,
    columns,
    pageSize: 5,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-5"></h2>
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/add-coupon')}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Coupon
        </Button>
      </div>
      <AdvanceTableProvider {...table}>
        <div className="border-y border-translucent">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default EcomVendorCouponTable;
